import Money from 'common/Money';
import {DateTime} from 'luxon';
import * as React from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceArea,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {toDisplayBalance} from 'src/account/balanceDisplay';
import {useUnitsById} from 'src/hooks/useUnits';
import {niceTimeTicks, spanDateFormatter} from 'src/lib/chartDateAxis';
import { formatMoneyWithUnits} from 'src/lib/unitDisplay';
import type {UnitsById} from 'src/lib/unitDisplay';

/** A point on the assembled server-side series (see BalanceHistory). */
type HistoryPoint = {date: string; balance: number; source: string};
type HistorySpan = {startDate: string; endDate: string};

type Props = {
  /**
   * The assembled balance history from the server: one step-line point per
   * changed day, already merged across readings, reconciled walks, estimated
   * tails, and anchors. Balances are in the stored sign convention; the chart
   * applies the credit-card flip.
   */
  points: ReadonlyArray<HistoryPoint>;
  /** Reconciled spans, drawn as bands behind the line. */
  spans: ReadonlyArray<HistorySpan>;
  /** True while the parent's query is loading. */
  loading: boolean;
  /** ISO 4217 currency code of the account (e.g. 'USD', 'EUR'). */
  currency: string;
  /**
   * Credit card balances are stored negative (liability convention). When true,
   * the chart plots the "amount owed" so the line rises as debt grows,
   * matching the "Amount owed" framing used elsewhere.
   */
  isCreditCard?: boolean;
};

// Custom units render through their Unit metadata ("3 passes"); non-ISO
// commodity currencies (TENNIS_CLASS) make Intl's currency style throw and
// fall back to a plain number with the code as a suffix, mirroring
// Money.toDecimalString's fallback.
const formatCurrency = (
  cents: number,
  currency: string,
  unitsById: UnitsById,
): string => {
  if (unitsById.has(currency)) {
    return formatMoneyWithUnits(Money.fromInt(cents, currency), unitsById);
  }
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(cents / 100);
  } catch {
    const bare = new Intl.NumberFormat('en-US', {
      maximumFractionDigits: 2,
    }).format(cents / 100);
    return `${bare} ${currency}`;
  }
};

// Compact axis labels ($1.2K, $304K) so the Y gutter stays narrow on mobile.
// The tooltip still shows the exact value. Custom units get the bare compact
// number — the unit label would widen the gutter, and the tooltip names it.
const formatCompactCurrency = (
  cents: number,
  currency: string,
  unitsById: UnitsById,
): string => {
  if (unitsById.has(currency)) {
    return new Intl.NumberFormat('en-US', {
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(cents / 100);
  }
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(cents / 100);
  } catch {
    return new Intl.NumberFormat('en-US', {
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(cents / 100);
  }
};

/** Where a point's balance came from — mirrors the server's source union. */
const SOURCE_LABELS: Record<string, string> = {
  reading: 'Synced balance',
  reconciled: 'Reconciled ledger',
  estimated: 'Estimated from transactions',
  anchor: 'Recorded balance',
  opening: 'Starting balance',
  checkpoint: 'Reconciled checkpoint',
};

// Matches the reconcile timeline's status colors: green for reconciled,
// amber for a recorded balance still awaiting reconciliation, sky for the
// starting balance (the timeline's label for it, and the opening-balance
// badge on the anchor detail page).
const CHECKPOINT_COLOR = '#16a34a';
const PENDING_ANCHOR_COLOR = '#d97706';
const OPENING_ANCHOR_COLOR = '#0284c7';
const LINE_COLOR = '#3b82f6';

type RangeKey = '1M' | '3M' | '6M' | '1Y' | 'ALL';
const RANGES: ReadonlyArray<{
  key: RangeKey;
  label: string;
  months: number | null;
}> = [
  {key: '1M', label: '1M', months: 1},
  {key: '3M', label: '3M', months: 3},
  {key: '6M', label: '6M', months: 6},
  {key: '1Y', label: '1Y', months: 12},
  {key: 'ALL', label: 'All', months: null},
];

type ChartRow = {
  timestamp: number;
  source: string;
  // The solid (verified/attested) line and the dashed (estimated) line are
  // separate series so they can be styled differently; a boundary point sits
  // on both so the dashed tail visually connects to the solid line.
  solidBalance: number | null;
  estimatedBalance: number | null;
  // Sparse series feeding the dot-only anchor overlays.
  checkpointBalance: number | null;
  pendingAnchorBalance: number | null;
  openingAnchorBalance: number | null;
  // The balance shown in the tooltip regardless of which series it belongs to.
  balance: number;
};

type TooltipPayload = {payload: ChartRow};
type CustomTooltipProps = {
  active?: boolean;
  payload?: TooltipPayload[];
  label?: number;
  currency: string;
  unitsById: UnitsById;
};

const CustomTooltip = ({
  active,
  payload,
  label,
  currency,
  unitsById,
}: CustomTooltipProps) => {
  if (!active || !payload?.length || label === undefined) return null;
  const point = payload[0].payload;
  return (
    <div className="bg-card border-border rounded-lg border px-3 py-2 shadow-md">
      <p className="text-muted-foreground mb-1 text-xs">
        {/* UTC to match the midnight-UTC data points and the (UTC) axis
            labels, so a hovered point reads the same date as its tick. */}
        {DateTime.fromMillis(label, {zone: 'utc'}).toFormat('MMM d, yyyy')}
      </p>
      <p className="text-foreground text-sm font-semibold">
        {formatCurrency(point.balance, currency, unitsById)}
      </p>
      <p className="text-muted-foreground mt-0.5 text-xs">
        {SOURCE_LABELS[point.source] ?? point.source}
      </p>
    </div>
  );
};

const toMillis = (isoDate: string): number =>
  DateTime.fromISO(isoDate, {zone: 'utc'}).toMillis();

export const BalanceHistoryChart = ({
  points,
  spans,
  loading,
  currency,
  isCreditCard,
}: Props) => {
  const unitsById = useUnitsById();
  const [range, setRange] = React.useState<RangeKey>('ALL');

  // Flatten to display-signed points sorted by day (the server already sorts,
  // but a defensive sort keeps the step line monotonic in time).
  const allPoints = React.useMemo(
    () =>
      [...points]
        .map((p) => ({
          timestamp: toMillis(p.date),
          balance: toDisplayBalance(p.balance, isCreditCard ?? false),
          source: p.source,
        }))
        .sort((a, b) => a.timestamp - b.timestamp),
    [points, isCreditCard],
  );

  // Range clip: keep points on/after the window start. A synthetic carry point
  // at the window edge holds the last pre-window balance so the step line fills
  // the whole width rather than starting partway in.
  const rangeStart = React.useMemo(() => {
    const months = RANGES.find((r) => r.key === range)?.months ?? null;
    if (months === null || allPoints.length === 0) return null;
    const last = allPoints[allPoints.length - 1].timestamp;
    return DateTime.fromMillis(last).minus({months}).toMillis();
  }, [range, allPoints]);

  const visiblePoints = React.useMemo((): Array<{
    timestamp: number;
    balance: number;
    source: string;
    isCarry?: boolean;
  }> => {
    if (rangeStart === null) return allPoints;
    const within = allPoints.filter((p) => p.timestamp >= rangeStart);
    const lastBefore = [...allPoints]
      .reverse()
      .find((p) => p.timestamp < rangeStart);
    if (
      lastBefore &&
      (within.length === 0 || within[0].timestamp > rangeStart)
    ) {
      // Carry the pre-window balance in at the window edge so the step line
      // fills the full width. It keeps the carried source (so its tooltip and
      // solid/dashed styling are right) but is flagged so it draws no dot.
      return [
        {
          timestamp: rangeStart,
          balance: lastBefore.balance,
          source: lastBefore.source,
          isCarry: true,
        },
        ...within,
      ];
    }
    return within;
  }, [allPoints, rangeStart]);

  // Split into the solid + dashed series, marking the boundary points that
  // belong to both so the dashed estimated tail connects to the solid line.
  const chartData: ChartRow[] = React.useMemo(() => {
    const isEstimated = (source: string) => source === 'estimated';
    return visiblePoints.map((p, i) => {
      const prev = visiblePoints[i - 1];
      const next = visiblePoints[i + 1];
      const estimatedHere = isEstimated(p.source);
      // A boundary point is an attested point adjacent to an estimated one.
      const touchesEstimated =
        (prev && isEstimated(prev.source)) ||
        (next && isEstimated(next.source));
      return {
        timestamp: p.timestamp,
        source: p.source,
        balance: p.balance,
        solidBalance: estimatedHere ? null : p.balance,
        estimatedBalance: estimatedHere || touchesEstimated ? p.balance : null,
        checkpointBalance:
          p.source === 'checkpoint' && !p.isCarry ? p.balance : null,
        pendingAnchorBalance:
          p.source === 'anchor' && !p.isCarry ? p.balance : null,
        openingAnchorBalance:
          p.source === 'opening' && !p.isCarry ? p.balance : null,
      };
    });
  }, [visiblePoints]);

  const visibleSpans = React.useMemo(
    () =>
      spans
        .map((s) => ({start: toMillis(s.startDate), end: toMillis(s.endDate)}))
        .filter((s) => rangeStart === null || s.end >= rangeStart),
    [spans, rangeStart],
  );

  if (!loading && allPoints.length < 2) {
    return (
      <div className="text-muted-foreground flex h-32 items-center justify-center text-sm">
        Record at least two balances to see a chart.
      </div>
    );
  }

  // Format axis labels from the span actually on screen so a multi-year range
  // shows years and a multi-month range shows "MMM yyyy" instead of an
  // ambiguous bare "Jun 9". For spans past a few months, pin ticks to calendar
  // boundaries so the coarser labels stay aligned and unique; shorter windows
  // keep Recharts' automatic day-level ticks.
  const firstTs = chartData[0]?.timestamp ?? 0;
  const lastTs = chartData[chartData.length - 1]?.timestamp ?? firstTs;
  const spanDays = (lastTs - firstTs) / 86_400_000;
  const tickFormatter = spanDateFormatter(firstTs, lastTs);
  const ticks = spanDays > 90 ? niceTimeTicks(firstTs, lastTs) : undefined;

  const hasCheckpoints = chartData.some((p) => p.checkpointBalance !== null);
  const hasPendingAnchors = chartData.some(
    (p) => p.pendingAnchorBalance !== null,
  );
  const hasOpeningAnchor = chartData.some(
    (p) => p.openingAnchorBalance !== null,
  );
  const hasEstimated = chartData.some((p) => p.source === 'estimated');

  return (
    <div>
      <div className="mb-2 flex justify-end">
        <div className="border-border inline-flex overflow-hidden rounded-lg border">
          {RANGES.map((r) => (
            <button
              key={r.key}
              onClick={() => setRange(r.key)}
              className={`px-2.5 py-1 text-xs transition ${
                range === r.key
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-muted'
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>
      <ResponsiveContainer width="100%" height={180}>
        <LineChart
          data={chartData}
          margin={{top: 4, right: 8, left: 0, bottom: 0}}
        >
          <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
          <XAxis
            dataKey="timestamp"
            scale="time"
            type="number"
            domain={['dataMin', 'dataMax']}
            ticks={ticks}
            tickFormatter={tickFormatter}
            tick={{fontSize: 11}}
            className="text-muted-foreground"
          />
          <YAxis
            tickFormatter={(cents: number) =>
              formatCompactCurrency(cents, currency, unitsById)
            }
            tick={{fontSize: 11}}
            width={52}
            className="text-muted-foreground"
          />
          <Tooltip
            content={<CustomTooltip currency={currency} unitsById={unitsById} />}
          />
          {/* Span bands render before the lines so the line draws on top.
              Adjacent spans share an anchor date and merge into one region;
              the green checkpoint dots keep the boundaries countable. */}
          {visibleSpans.map((span) => (
            <ReferenceArea
              key={`span-${span.start}`}
              x1={span.start}
              x2={span.end}
              fill={CHECKPOINT_COLOR}
              fillOpacity={0.08}
              stroke="none"
            />
          ))}
          {/* Estimated tails (before the earliest / after the latest attested
              balance) render as a dashed line — a best guess, not verified. */}
          <Line
            type="stepAfter"
            dataKey="estimatedBalance"
            stroke={LINE_COLOR}
            strokeWidth={2}
            strokeDasharray="4 3"
            dot={false}
            activeDot={{r: 4}}
            isAnimationActive={false}
            connectNulls={false}
            name="Estimated"
          />
          {/* An end-of-day balance holds until the next change, so the line
              steps rather than sloping through days that had no activity. */}
          <Line
            type="stepAfter"
            dataKey="solidBalance"
            stroke={LINE_COLOR}
            strokeWidth={2}
            dot={chartData.length <= 30}
            activeDot={{r: 4}}
            isAnimationActive={false}
            connectNulls={false}
            name="Balance"
          />
          {/* Anchor markers: user-attested balances as standalone dots on top
              of the line — green when verified by a reconciliation, amber
              while still awaiting one. */}
          <Line
            dataKey="checkpointBalance"
            stroke="none"
            dot={{r: 4, fill: CHECKPOINT_COLOR, strokeWidth: 0}}
            activeDot={false}
            isAnimationActive={false}
            name="Reconciled checkpoint"
          />
          <Line
            dataKey="pendingAnchorBalance"
            stroke="none"
            dot={{r: 4, fill: PENDING_ANCHOR_COLOR, strokeWidth: 0}}
            activeDot={false}
            isAnimationActive={false}
            name="Recorded balance"
          />
          {/* The starting balance: nothing is dated before it, so it is not
              an anchor awaiting a period — it's where the history begins. */}
          <Line
            dataKey="openingAnchorBalance"
            stroke="none"
            dot={{r: 4, fill: OPENING_ANCHOR_COLOR, strokeWidth: 0}}
            activeDot={false}
            isAnimationActive={false}
            name="Starting balance"
          />
        </LineChart>
      </ResponsiveContainer>
      {(hasCheckpoints ||
        hasPendingAnchors ||
        hasOpeningAnchor ||
        hasEstimated) && (
        <div className="text-muted-foreground mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
          {visibleSpans.length > 0 && (
            <span className="inline-flex items-center gap-1.5">
              <span
                className="inline-block h-2.5 w-3.5 rounded-xs"
                style={{backgroundColor: CHECKPOINT_COLOR, opacity: 0.15}}
              />
              Reconciled span
            </span>
          )}
          {hasCheckpoints && (
            <span className="inline-flex items-center gap-1.5">
              <span
                className="inline-block size-2 rounded-full"
                style={{backgroundColor: CHECKPOINT_COLOR}}
              />
              Reconciled checkpoint
            </span>
          )}
          {/* Ordered as the line reads left to right: where the history
              starts, then the anchors still waiting on it. */}
          {hasOpeningAnchor && (
            <span className="inline-flex items-center gap-1.5">
              <span
                className="inline-block size-2 rounded-full"
                style={{backgroundColor: OPENING_ANCHOR_COLOR}}
              />
              Starting balance
            </span>
          )}
          {hasPendingAnchors && (
            <span className="inline-flex items-center gap-1.5">
              <span
                className="inline-block size-2 rounded-full"
                style={{backgroundColor: PENDING_ANCHOR_COLOR}}
              />
              Not yet reconciled
            </span>
          )}
          {hasEstimated && (
            <span className="inline-flex items-center gap-1.5">
              <svg width="14" height="6" className="inline-block">
                <line
                  x1="0"
                  y1="3"
                  x2="14"
                  y2="3"
                  stroke={LINE_COLOR}
                  strokeWidth="2"
                  strokeDasharray="4 3"
                />
              </svg>
              Estimated
            </span>
          )}
        </div>
      )}
    </div>
  );
};
